import { useTheme } from '@/src/context/ThemeContext';
import { UseCaseLandingConfig, UseCaseLandingScreen } from '@/src/presentation/screens/UseCaseLandingScreen';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';

const PAGE_URL = 'https://tournaly.app/single-elimination-bracket';
const OG_IMAGE = 'https://tournaly.app/og-image.png';

const CONFIG: UseCaseLandingConfig = {
  badge: 'Single Elimination',
  badgeEs: 'Eliminación Simple',
  badgeIcon: 'git-branch-outline',
  title: 'Single Elimination',
  titleEs: 'Brackets de',
  titleAccent: 'Bracket Maker',
  titleAccentEs: 'Eliminación Simple',
  subtitle:
    'Build clean single-elimination brackets instantly. One loss and you are out — the fastest, most decisive tournament format. Free for up to 16 players.',
  subtitleEs:
    'Crea brackets de eliminación simple al instante. Una derrota y quedas eliminado — el formato más rápido y decisivo. Gratis hasta 16 jugadores.',
  steps: [
    {
      title: 'Add your participants',
      titleEs: 'Añade participantes',
      desc: 'Enter player names manually, invite by email or share your unique invite code.',
      descEs: 'Introduce nombres manualmente, invita por email o comparte tu código de invitación.',
    },
    {
      title: 'Auto-generate the bracket',
      titleEs: 'Genera el bracket automáticamente',
      desc: 'Participants are seeded automatically. The full bracket is built in one tap.',
      descEs: 'Los participantes reciben cabeza de serie automáticamente. El bracket completo en un toque.',
    },
    {
      title: 'Update results in real time',
      titleEs: 'Actualiza resultados en tiempo real',
      desc: 'Enter scores after each match. The bracket advances and standings update instantly.',
      descEs: 'Introduce puntuaciones tras cada partido. El bracket avanza y la clasificación se actualiza al instante.',
    },
  ],
  features: [
    {
      icon: 'git-branch-outline',
      color: '#FF6B35',
      title: 'Automatic Seeding',
      titleEs: 'Cabezas de Serie Automáticas',
      desc: 'Participants are seeded and matched up automatically — no manual bracket drawing.',
      descEs: 'Los participantes se ordenan y emparejan automáticamente — sin dibujar brackets a mano.',
    },
    {
      icon: 'flash-outline',
      color: '#4ECDC4',
      title: 'Instant Bracket Generation',
      titleEs: 'Generación Instantánea',
      desc: 'From 4 to 512 participants — the bracket is ready in seconds.',
      descEs: 'De 4 a 512 participantes — el bracket listo en segundos.',
    },
    {
      icon: 'share-social-outline',
      color: '#06B6D4',
      title: 'Export as PNG or PDF',
      titleEs: 'Exportar como PNG o PDF',
      desc: 'Download a clean bracket image or PDF to print or share on social media.',
      descEs: 'Descarga una imagen o PDF del bracket para imprimir o compartir en redes.',
    },
    {
      icon: 'link-outline',
      color: '#10B981',
      title: 'Shareable Tournament Link',
      titleEs: 'Enlace Compartible',
      desc: 'Every tournament gets a unique link. Share it so anyone can follow the results.',
      descEs: 'Cada torneo tiene un enlace único. Compártelo para que todos sigan los resultados.',
    },
    {
      icon: 'bar-chart-outline',
      color: '#45B7D1',
      title: 'Live Bracket Updates',
      titleEs: 'Actualizaciones en Vivo',
      desc: 'Results are reflected instantly — no page refresh needed.',
      descEs: 'Los resultados se reflejan al instante — sin recargar la página.',
    },
    {
      icon: 'star-outline',
      color: '#FFD700',
      title: 'Leagues & Seasons',
      titleEs: 'Ligas y Temporadas',
      desc: 'Chain multiple single-elimination tournaments into a league with cumulative standings.',
      descEs: 'Encadena varios torneos en una liga con clasificación acumulada.',
    },
  ],
  relatedBlogSlug: '2026-03-06-single-vs-double-elimination',
  relatedBlogTitle: 'Single vs Double Elimination: Which Format Should You Use?',
  relatedBlogTitleEs: 'Eliminación Simple vs Doble: ¿Cuál Deberías Usar?',
  relatedBlogSummary: 'A deep comparison of both formats to help you choose the right one for your tournament.',
  relatedBlogSummaryEs: 'Una comparación detallada de ambos formatos para ayudarte a elegir el adecuado para tu torneo.',
};

const title = 'Single Elimination Bracket Maker — Free Online Tool | Tournaly';
const description =
  'Create single elimination tournament brackets online for free. Auto-seeding, real-time results, PNG/PDF export. Up to 16 players free, up to 512 with Pro.';
const titleEs = 'Creador de Brackets de Eliminación Simple Gratis — Tournaly';
const descriptionEs =
  'Crea brackets de eliminación simple online gratis. Cabezas de serie automáticas, resultados en tiempo real, exportar PNG/PDF. Hasta 16 jugadores gratis.';

export default function SingleEliminationBracketPage() {
  const { colors, isDark, toggleTheme } = useTheme();
  const router = useRouter();

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Tournaly — Single Elimination Bracket Maker',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web, iOS, Android',
    url: PAGE_URL,
    description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Tournaly', url: 'https://tournaly.app' },
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tournaly" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="keywords" content="single elimination bracket, single elimination tournament, bracket maker, tournament bracket, elimination bracket generator" />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <UseCaseLandingScreen
        config={CONFIG}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onGetStarted={() => router.push('/auth/register' as any)}
        onSignIn={() => router.push('/auth/login' as any)}
        onBlogPostPress={(slug) => {
          if (slug === 'blog') router.push('/blog' as any);
          else if (slug === 'privacy') router.push('/privacy' as any);
          else if (slug === 'terms') router.push('/terms' as any);
          else router.push(`/blog/${slug}` as any);
        }}
        colors={colors}
      />
    </>
  );
}
